/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Shared builder for the JSON-LD structured data emitted in each site's
 * `<head>` (rendered by `FavIcons`). Centralizing this removes the per-site
 * copy-paste that previously caused identity bugs (a shared dead `@id`, brands
 * inheriting Epic's identity) and gives us a single, safe place to serialize
 * the markup.
 *
 * Each site provides a small config; the builder assembles the `Organization`
 * and `WebSite` nodes (plus an optional `SoftwareApplication` node) and returns
 * JSON-LD-safe strings ready to inline.
 */

const SCHEMA_CONTEXT = "https://schema.org/";
const DEFAULT_LEGAL_NAME = "Epic Games, Inc.";

/** Epic Games HQ — shared by every Epic-owned brand. */
const EPIC_ADDRESS = {
	"@type": "PostalAddress",
	addressLocality: "Cary",
	addressRegion: "NC",
	addressCountry: "US",
	postalCode: "27518",
	streetAddress: "620 Crossroads Blvd",
} as const;

const EPIC_CONTACT_POINT = [
	{
		"@type": "ContactPoint",
		telephone: "+1-919-854-0070",
		contactType: "customer service",
	},
] as const;

export interface SoftwareApplicationConfig {
	/** schema.org `applicationCategory`, e.g. `"DeveloperApplication"`. */
	applicationCategory: string;
	/** Human-readable OS list, e.g. `"Windows, macOS, Linux"`. */
	operatingSystem: string;
}

export interface StructuredDataConfig {
	/** Brand / product name, used as the `Organization` + `WebSite` name. */
	name: string;
	/** Shared description for the `Organization` + `WebSite` nodes. */
	description: string;
	/** Bare production origin, e.g. `"https://www.unrealengine.com"` (no trailing slash). */
	url: string;
	/** Absolute URL to the brand logo / social image. Omitted from output when unset. */
	logo?: string;
	/** Registered legal entity. Defaults to `"Epic Games, Inc."`. */
	legalName?: string;
	/** Verified official social/profile URLs for the `Organization`'s `sameAs`. */
	sameAs?: string[];
	/** When set, emits a `SoftwareApplication` node for the product. */
	softwareApplication?: SoftwareApplicationConfig;
	/**
	 * When `true`, adds a sitelinks-search-box `SearchAction` to the `WebSite`
	 * node, targeting `${url}/search?keyword={search_term_string}`. Only enable
	 * for sites with a working on-site search results page — Google flags a
	 * target that does not resolve to a real search page.
	 */
	searchAction?: boolean;
}

export interface StructuredData {
	organization: string;
	website: string;
	softwareApplication?: string;
}

/**
 * Serialize a value to a string safe to inline inside a
 * `<script type="application/ld+json">` rendered via `dangerouslySetInnerHTML`.
 *
 * `JSON.stringify` handles quote/backslash escaping; additionally escaping `<`
 * prevents a value containing `</script>` from breaking out of the surrounding
 * element (secure-coding V3.2 — Unintended Content Interpretation). The current
 * inputs are all static, but centralizing this keeps the emission safe if a
 * value ever becomes dynamic.
 */
export const jsonLdSafeStringify = (value: unknown): string => JSON.stringify(value).replace(/</g, "\\u003c");

export const buildStructuredData = (config: StructuredDataConfig): StructuredData => {
	const {
		name,
		description,
		url,
		logo,
		legalName = DEFAULT_LEGAL_NAME,
		sameAs,
		softwareApplication,
		searchAction,
	} = config;

	const organization = {
		"@context": SCHEMA_CONTEXT,
		"@type": "Organization",
		"@id": `${url}/#organization`,
		legalName,
		name,
		description,
		url,
		address: EPIC_ADDRESS,
		...(logo ? { logo } : {}),
		contactPoint: EPIC_CONTACT_POINT,
		...(sameAs && sameAs.length > 0 ? { sameAs } : {}),
	};

	const website = {
		"@context": SCHEMA_CONTEXT,
		"@type": "WebSite",
		"@id": `${url}/#website`,
		name,
		description,
		url,
		...(searchAction
			? {
					potentialAction: {
						"@type": "SearchAction",
						target: {
							"@type": "EntryPoint",
							urlTemplate: `${url}/search?keyword={search_term_string}`,
						},
						"query-input": "required name=search_term_string",
					},
				}
			: {}),
	};

	const structuredData: StructuredData = {
		organization: jsonLdSafeStringify(organization),
		website: jsonLdSafeStringify(website),
	};

	if (softwareApplication) {
		structuredData.softwareApplication = jsonLdSafeStringify({
			"@context": SCHEMA_CONTEXT,
			"@type": "SoftwareApplication",
			"@id": `${url}/#software`,
			name,
			applicationCategory: softwareApplication.applicationCategory,
			operatingSystem: softwareApplication.operatingSystem,
			description,
			url,
			...(logo ? { image: logo } : {}),
			publisher: { "@id": `${url}/#organization` },
		});
	}

	return structuredData;
};
